import type { PostgrestError } from '@supabase/supabase-js';
import { getProdSupabaseClient } from './client';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import {
  getTenantIdFromSession,
  isInternalTenant,
  getDealerAggregatesForTenant,
  type DealerAggregate
} from './tenantScoping';
import { isTenantAccessError, TenantAccessError } from './rpcErrors';

export interface Dealer {
  /** Unique row id: prod.dealers.id, or a synthetic name|lat|lng key for internal tenants */
  dealer_id: string;
  dealer_name: string;
  location: string;
  address: string;
  /** Open recalls (campaign × vehicle), as the recall map counts them; undefined until sent */
  recalls: number | undefined;
  /** Vehicles with at least one open recall */
  vehicles: number;
  campaigns: number;
  status: 'included' | 'excluded';
  phone?: string | undefined;
  website?: boolean | undefined;
  is_included?: boolean | undefined;
  notes?: string | undefined;
  seller_latitude?: number | undefined;
  seller_longitude?: number | undefined;
  // Additional fields from view
  seller_address?: string | undefined;
  seller_city?: string | undefined;
  seller_region?: string | undefined;
  seller_postal_code?: string | undefined;
  dealer_type?: string | undefined;
  seller_rating?: number | undefined;
  detail_url?: string | undefined;
  advisor_notes?: string | undefined;
  technician_notes?: string | undefined;
  /** Every spelling this dealer is listed under, including dealer_name */
  alias_names: string[];
  /** Per-OEM counts keyed by OEM name, plus '_other'; {} until the backend computes them */
  oem_stats: OemStats;
  /** Internal (demo) tenants list dealers from aggregates; their settings are read-only */
  is_internal: boolean;
  /** Every number this dealer is listed with, main first; absent for internal tenants */
  phone_numbers?: DealerPhone[] | undefined;
}

export type OemStats = Record<
  string,
  { recalls: number | undefined; vehicles: number; campaigns: number }
>;

/** oem_stats as get_tenant_dealers_v2 sends it: `recalls` there is the vehicle count */
type RawOemStats = Record<string, { recalls: number; campaigns: number; recall_count?: number }>;

/**
 * One phone number from get_tenant_dealers_v2.phone_numbers. The backend
 * de-dupes, formats and orders the list (main number first): render as is.
 */
export interface DealerPhone {
  /** Unique within the list: digits + optional 'x' + extension */
  key: string;
  /** Always exactly 10 digits */
  digits: string;
  ext: string | null;
  /** Formatted for display, e.g. "(813) 555-1234 ext. 5" */
  display: string;
  /** Listings carrying it at its last sighting (0 = known only from an older record) */
  vins: number;
  /** YYYY-MM-DD */
  first_seen: string;
  /** YYYY-MM-DD */
  last_seen: string;
}

/** Row of prod.get_tenant_dealers_v2: one row per canonical dealer, settings tenant-wide */
export interface V2Dealer {
  dealer_id: string;
  dealer_name: string;
  seller_address: string | null;
  seller_city: string | null;
  seller_region: string | null;
  seller_postal_code: string | null;
  seller_latitude: number | null;
  seller_longitude: number | null;
  phone_number: string | null;
  detail_url: string | null;
  seller_type: string | null;
  seller_rating: number | null;
  /** Vehicles with an open recall (the column predates recall_count) */
  total_recalls: number;
  /** Vehicle × campaign pairs; absent before the backend adds it */
  recall_count?: number;
  unique_campaigns: number;
  oem_stats: RawOemStats | null;
  alias_names: string[] | null;
  is_included: boolean | null;
  notes: string | null;
  advisor_notes: string | null;
  technician_notes: string | null;
  notes_updated_at: string | null;
  /** Listed in the latest snapshot; inactive dealers are hidden but keep their settings */
  is_active: boolean;
  last_snapshot_date: string | null;
  /** vehixdb-prod #256; never null from the RPC, absent before that migration */
  phone_numbers?: DealerPhone[];
}

type ProdClient = ReturnType<typeof getProdSupabaseClient>;

/**
 * The prod client is created without generated Database types, so supabase-js
 * infers every rpc() as taking no arguments and returning `never`. This narrows
 * one call to the shape the SQL function actually has.
 */
export function callProdRpc<T>(client: ProdClient, fn: string, args?: Record<string, unknown>) {
  const rpc = client.rpc as unknown as (
    this: ProdClient,
    fn: string,
    args?: Record<string, unknown>
  ) => PromiseLike<{ data: T | null; error: PostgrestError | null }>;
  return rpc.call(client, fn, args);
}

export async function getTenantId(): Promise<string | null> {
  const { supabase } = await import('./client');
  return getTenantIdFromSession(supabase);
}

/**
 * Transform DealerAggregate to Dealer interface
 */
function transformAggregateToDealer(agg: DealerAggregate): Dealer {
  const location =
    agg.dealer_city && agg.dealer_state
      ? `${agg.dealer_city}, ${agg.dealer_state}`
      : agg.dealer_city || agg.dealer_state || 'Unknown Location';

  const address = [agg.dealer_address, agg.dealer_city, agg.dealer_state]
    .filter(Boolean)
    .join(', ');

  return {
    dealer_id: [agg.dealer_name, agg.dealer_lat ?? '', agg.dealer_lon ?? ''].join('|'),
    dealer_name: agg.dealer_name,
    location,
    address,
    recalls: agg.recall_count,
    vehicles: agg.vehicle_count,
    campaigns: agg.campaign_count,
    status: 'included' as const,
    phone: agg.dealer_phone || undefined,
    website: false, // Not available in aggregates
    is_included: true, // Default for internal tenants
    notes: undefined,
    seller_latitude: agg.dealer_lat ?? undefined,
    seller_longitude: agg.dealer_lon ?? undefined,
    seller_address: agg.dealer_address || undefined,
    seller_city: agg.dealer_city || undefined,
    seller_region: agg.dealer_state || undefined,
    seller_postal_code: agg.dealer_zip || undefined,
    dealer_type: undefined,
    seller_rating: undefined,
    alias_names: [agg.dealer_name],
    oem_stats: {},
    is_internal: true
  };
}

/**
 * Get all dealers for internal tenants using SECURITY DEFINER RPC
 * Uses get_dealer_aggregates_for_viewport_latest_per_zone which bypasses RLS
 */

async function getAllDealersForInternalTenant(
  prodClient: ReturnType<typeof getProdSupabaseClient>,
  tenantId: string
): Promise<Dealer[]> {
  // Use the existing SECURITY DEFINER RPC function
  const { data, error } = await getDealerAggregatesForTenant(prodClient, tenantId, {
    // No viewport bounds = get all dealers
    limit: 10000 // Higher limit for dealer management
  });

  if (error) {
    // If NOT_INTERNAL, shouldn't happen since we checked, but handle gracefully
    if (error.code === 'NOT_INTERNAL') {
      return [];
    }
    throw new Error(error.message);
  }

  if (!data) {
    return [];
  }

  // Transform DealerAggregate to Dealer interface
  return data.map(transformAggregateToDealer);
}

/**
 * Build location string from city and region
 */
function buildLocation(city: string | null, region: string | null): string {
  if (city && region) {
    return `${city}, ${region}`;
  }
  return city || region || 'Unknown Location';
}

/**
 * Build address string from parts
 */
function buildAddress(parts: (string | null)[]): string {
  return parts.filter(Boolean).join(', ');
}

function normalizeOemStats(raw: RawOemStats | null): OemStats {
  return Object.fromEntries(
    Object.entries(raw ?? {}).map(([oem, stats]) => [
      oem,
      { recalls: stats.recall_count, vehicles: stats.recalls, campaigns: stats.campaigns }
    ])
  );
}

/**
 * Transform a get_tenant_dealers_v2 row to the Dealer interface
 */
// eslint-disable-next-line complexity -- flat field mapping; each `||` counts as a branch
export function transformV2Dealer(row: V2Dealer): Dealer {
  const isIncluded = row.is_included ?? true;
  const aliasNames = row.alias_names?.length ? row.alias_names : [row.dealer_name];
  const phoneNumbers = row.phone_numbers ?? [];

  return {
    dealer_id: row.dealer_id,
    dealer_name: row.dealer_name,
    location: buildLocation(row.seller_city, row.seller_region),
    address: buildAddress([row.seller_address, row.seller_city, row.seller_region]),
    // No fallback to total_recalls: that is a vehicle count, not a recall count
    recalls: row.recall_count,
    vehicles: row.total_recalls,
    campaigns: row.unique_campaigns,
    status: isIncluded ? 'included' : 'excluded',
    phone: phoneNumbers[0]?.display || row.phone_number || undefined,
    phone_numbers: phoneNumbers,
    website: !!row.detail_url,
    is_included: isIncluded,
    notes: row.notes || undefined,
    advisor_notes: row.advisor_notes || undefined,
    technician_notes: row.technician_notes || undefined,
    seller_latitude: row.seller_latitude ?? undefined,
    seller_longitude: row.seller_longitude ?? undefined,
    seller_address: row.seller_address || undefined,
    seller_city: row.seller_city || undefined,
    seller_region: row.seller_region || undefined,
    seller_postal_code: row.seller_postal_code || undefined,
    dealer_type: row.seller_type || undefined,
    seller_rating: row.seller_rating ?? undefined,
    detail_url: row.detail_url || undefined,
    alias_names: aliasNames,
    oem_stats: normalizeOemStats(row.oem_stats),
    is_internal: false
  };
}

export const dealerAPI = {
  // Dealers for the config screen.
  // Internal tenants: dealer aggregates RPC (no zone filtering), read-only settings.
  // Dealer tenants: get_tenant_dealers_v2 (verifies the JWT tenant) returns one row
  // per canonical dealer with tenant-level settings; only active dealers are shown.
  async getDealersWithPreferences(_userId: string): Promise<Dealer[]> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        return [];
      }

      const prodClient = getProdSupabaseClient();

      // Internal tenants (like Demo) see ALL dealers without zone restrictions
      if (await isInternalTenant(prodClient, tenantId)) {
        return await getAllDealersForInternalTenant(prodClient, tenantId);
      }

      const { data, error } = await callProdRpc<V2Dealer[]>(prodClient, 'get_tenant_dealers_v2', {
        p_tenant_id: tenantId
      });

      if (error) {
        if (isTenantAccessError(error)) throw new TenantAccessError();
        logSystemError(error, 'getting dealers (v2)');
        throw translateSystemError(error, 'getting dealers (v2)');
      }

      // Inactive dealers are not in the latest snapshot: hidden, never deleted,
      // and they come back with their settings when they reappear.
      return (data || []).filter(row => row.is_active).map(transformV2Dealer);
    } catch (error) {
      if (error instanceof TenantAccessError) throw error;
      if (isSystemError(error)) {
        logSystemError(error, 'getting dealers with preferences');
        throw translateSystemError(error, 'getting dealers with preferences');
      }
      throw error;
    }
  },

  // Sync dealers from prod.cars_with_safety_recalls_mv to prod.dealers
  // This extracts unique dealers from the materialized view and upserts them to the dealers table
  async syncDealers(): Promise<{
    inserted_count: number;
    updated_count: number;
  }> {
    try {
      const tenantId = await getTenantId();
      if (!tenantId) {
        throw new Error('Tenant ID not available');
      }

      const prodClient = getProdSupabaseClient();

      // Call the RPC function to sync dealers
      const { data, error } = await callProdRpc<
        Array<{ inserted_count: number | null; updated_count: number | null }>
      >(prodClient, 'sync_dealers_from_mv');

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'syncing dealers from materialized view');
          throw translateSystemError(error, 'syncing dealers from materialized view');
        }
        throw error;
      }

      // The RPC function returns a table with one row containing the counts
      const counts = data?.[0];
      return {
        inserted_count: counts?.inserted_count || 0,
        updated_count: counts?.updated_count || 0
      };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'syncing dealers');
        throw translateSystemError(error, 'syncing dealers');
      }
      throw error;
    }
  }
};
